/**
 * "Propose change" (HF2, H8 `propose-change`): the host owns these fields (Q87), so HR (or the
 * person, in self-service) proposes the new values and the host applies them in its own records.
 * Only the fields that differ from today's are sent. A proposed work email is applied by the host
 * in its own staff edit; it never changes anyone's sign-in here (Q112).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import ThemeScope from '@/pages/payScales/ThemeScope';
import { employeesApi } from '@/api/endpoints/hr';
import {
  interplayError, interplayPeopleApi, type HostOwner, type InterplayErrorInfo,
} from '@/api/endpoints/interplayPeople';
import {
  changedFields, errorText, ownersLabel, peopleKeys, PROPOSE_KEYS, PROPOSE_REQUIRED, type HostBrand, type ProposeCurrent,
  type ProposeFormKey,
} from './model';

export type { ProposeCurrent };
const KEYS = PROPOSE_KEYS;
const REQUIRED = PROPOSE_REQUIRED;
type FormKey = ProposeFormKey;
const norm = (v: string | null | undefined) => (v ?? '').trim();

export default function ProposeChangeDialog({
  open,
  onClose,
  mode,
  employeeId,
  current,
  owners,
  brands,
  focusField,
}: {
  open: boolean;
  onClose: () => void;
  mode: 'hr' | 'ess';
  employeeId?: string;
  current: ProposeCurrent;
  owners: HostOwner[];
  brands: Record<string, HostBrand>;
  focusField?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const host = ownersLabel(t, owners, brands);
  const initial = useMemo(
    () => Object.fromEntries(KEYS.map((k) => [k, current[k] ?? ''])) as Record<FormKey, string>,
    [current],
  );
  const [form, setForm] = useState<Record<FormKey, string>>(initial);
  const [note, setNote] = useState('');
  const [error, setError] = useState<InterplayErrorInfo | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  const { data: departments } = useQuery({
    queryKey: ['interplay-people', 'departments'],
    queryFn: () => employeesApi.listDepartments().then((r) => r.data?.items ?? []),
    // HR only: /hr/departments needs HR rights; self-service users must never get a 403 for it.
    enabled: open && mode === 'hr',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const changes = changedFields(current, form);
  const changedKeys = Object.keys(changes);
  const missing = REQUIRED.filter((k) => k in changes && !norm(form[k]));
  const emailChanged = 'work_email' in changes;

  const mutation = useMutation({
    mutationFn: () => {
      const body = { changes, ...(note.trim() ? { note: note.trim() } : {}) };
      return mode === 'hr'
        ? interplayPeopleApi.proposeChange(employeeId!, body)
        : interplayPeopleApi.myProposeChange(body);
    },
    onSuccess: (r) => {
      setError(null);
      setSent(r.data?.proposals?.length ?? 1);
      void qc.invalidateQueries({ queryKey: peopleKeys.all });
    },
    onError: (e) => setError(interplayError(e)),
  });

  const fieldError = (k: FormKey): string | undefined => {
    if (missing.includes(k)) return t('interplay_people.propose.required');
    if (error?.fields.includes(k)) {
      return error.code === 'unit_not_mapped'
        ? t('interplay_people.error.unit_not_mapped', { host })
        : t('interplay_people.propose.invalid');
    }
    return undefined;
  };
  const text = (k: FormKey, extra: Record<string, unknown> = {}) => ({
    label: t(`interplay_people.field.${k}`),
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value })),
    error: !!fieldError(k),
    helperText: fieldError(k),
    fullWidth: true,
    size: 'small' as const,
    autoFocus: focusField === k,
    disabled: sent !== null,
    ...extra,
  });

  return (
    <ThemeScope>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="propose-change-dialog">
      <BrandedDialogTitle>{t('interplay_people.propose.title')}</BrandedDialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t(mode === 'hr' ? 'interplay_people.propose.intro_hr' : 'interplay_people.propose.intro_ess', { host })}
          </Typography>
          {sent !== null && (
            <Alert severity="success" data-testid="propose-sent">
              {t('interplay_people.propose.sent', { count: sent, host })}
            </Alert>
          )}
          {error && (
            <Alert severity={error.code === 'nothing_changed' ? 'info' : 'error'} data-testid="propose-error">
              {errorText(t, error, { host })}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
            <TextField {...text('first_name')} required />
            <TextField {...text('middle_name')} />
            <TextField {...text('last_name')} required />
          </Stack>
          <TextField
            {...text('work_email', { type: 'email' })}
            helperText={fieldError('work_email') ?? (emailChanged ? t('interplay_people.propose.email_note', { host }) : undefined)}
          />
          {emailChanged && !fieldError('work_email') && (
            <Alert severity="info" data-testid="email-note">{t('interplay_people.propose.email_note_long', { host })}</Alert>
          )}
          <TextField {...text('phone', { type: 'tel' })} />
          <TextField {...text('designation')} />
          {Array.isArray(departments) && departments.length > 0 && (
            <TextField {...text('department_id', { select: true })}>
              <MenuItem value="">{t('interplay_people.propose.no_department')}</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label={t('interplay_people.propose.note')}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={sent !== null}
            helperText={t('interplay_people.propose.note_help')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{sent !== null ? t('interplay_people.close') : t('interplay_people.cancel')}</Button>
        {sent === null && (
          <Button
            variant="contained"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || changedKeys.length === 0 || missing.length > 0}
          >
            {mutation.isPending ? t('interplay_people.sending') : t('interplay_people.propose.send', { host })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </ThemeScope>
  );
}
